import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { reviews } from '../data/menuData';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

const ReviewCard = ({ review, index }) => {
  const colors = ['bg-santomato-yellow', 'bg-santomato-red', 'bg-white', 'bg-santomato-accent'];
  const textColors = ['text-black', 'text-white', 'text-black', 'text-black'];
  const colorIndex = index % colors.length;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className={`card-brutal ${colors[colorIndex]} p-6 md:p-8 h-full`}
      data-testid={`review-${review.id}`}
    >
      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {[...Array(review.rating)].map((_, i) => (
          <Star key={i} size={24} className="fill-current text-black" />
        ))}
      </div>
      
      {/* Quote */}
      <p className={`font-nunito font-bold text-lg md:text-xl ${textColors[colorIndex]} mb-6 leading-relaxed`}>
        "{review.text}"
      </p>
      
      {/* Author */}
      <p className={`font-fredoka font-bold ${textColors[colorIndex]} uppercase`}>
        — {review.author}
      </p>
    </motion.div>
  );
};

const Reviews = () => {
  return (
    <section 
      id="reviews"
      data-testid="reviews-section"
      className="py-16 md:py-24 bg-santomato-cream relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 
            className="font-fredoka font-bold text-4xl sm:text-5xl md:text-6xl text-black uppercase mb-4"
            data-testid="reviews-title"
          >
            COSA DICONO
            <br />
            <span className="text-santomato-red">DI NOI</span>
          </h2>
        </motion.div>

        {/* Reviews Carousel for mobile, Grid for desktop */}
        <div className="block md:hidden">
          <Carousel className="w-full" data-testid="reviews-carousel-mobile">
            <CarouselContent>
              {reviews.map((review, index) => (
                <CarouselItem key={review.id} className="basis-[85%]">
                  <ReviewCard review={review} index={index} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-0 border-4 border-black bg-white" />
            <CarouselNext className="right-0 border-4 border-black bg-white" />
          </Carousel>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <ReviewCard key={review.id} review={review} index={index} />
          ))}
        </div>

        {/* Social CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="font-nunito text-gray-600 mb-4">
            Condividi il gusto! Scatta una foto e taggaci
          </p>
          <a 
            href="https://instagram.com/santomatopizza" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block font-fredoka font-bold text-xl text-santomato-red hover:underline"
            data-testid="instagram-link"
          >
            @santomatopizza
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Reviews;
